/**
 * COMPANY DASHBOARD SECTION
 * 
 * Full dashboard section for managing company accounts.
 * Shows list of companies, allows registration, and provides
 * access to company-specific features.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import { CompanyAccountService } from '../../lib/services/companyAccountService';
import { CompanyRegistrationForm } from './CompanyRegistrationForm';
import { CompanyDocumentUpload } from './CompanyDocumentUpload';
import { OfficerManagement } from './OfficerManagement';
import { CompanyDetailsEditor } from './CompanyDetailsEditor';
import { ShareTransferToCompany } from './ShareTransferToCompany';
import { sendKYCSubmittedEmail } from '../../lib/services/companyEmailService';
import type { CompanyAccount, CompanyKYCStatus, CompanyOfficer, CompanyDocument } from '../../types/company';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface CompanyWithDetails extends CompanyAccount {
  officers?: CompanyOfficer[];
  documents?: CompanyDocument[];
}

type DetailTab = 'info' | 'officers' | 'documents';

interface CompanyDashboardSectionProps {
  userId: number;
  onRefreshData?: () => Promise<void>;
}

// KYC Status configuration
const kycStatusConfig: Record<CompanyKYCStatus, { label: string; color: string; bgColor: string; icon: string }> = {
  draft: { label: 'Draft', color: 'text-white', bgColor: 'bg-gray-700', icon: '📝' },
  pending_documents: { label: 'Pending Documents', color: 'text-yellow-400', bgColor: 'bg-yellow-900/30', icon: '📄' },
  pending_review: { label: 'Under Review', color: 'text-blue-400', bgColor: 'bg-blue-900/30', icon: '🔍' },
  additional_info_required: { label: 'Info Required', color: 'text-orange-400', bgColor: 'bg-orange-900/30', icon: '⚠️' },
  approved: { label: 'Verified', color: 'text-green-400', bgColor: 'bg-green-900/30', icon: '✅' },
  rejected: { label: 'Rejected', color: 'text-red-400', bgColor: 'bg-red-900/30', icon: '❌' },
  suspended: { label: 'Suspended', color: 'text-red-400', bgColor: 'bg-red-900/30', icon: '🚫' },
  expired: { label: 'Expired', color: 'text-orange-400', bgColor: 'bg-orange-900/30', icon: '⏰' }
};

export const CompanyDashboardSection: React.FC<CompanyDashboardSectionProps> = ({
  userId,
  onRefreshData
}) => {
  const [companies, setCompanies] = useState<CompanyAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithDetails | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('info');
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [submittingKYC, setSubmittingKYC] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showOfficerManagement, setShowOfficerManagement] = useState(false);
  const [showDetailsEditor, setShowDetailsEditor] = useState(false);
  const [showShareTransfer, setShowShareTransfer] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const logoInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCompanies();
  }, [userId]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await CompanyAccountService.getUserCompanies(userId);

      if (result.success && result.data) {
        setCompanies(result.data);
      } else {
        setError(result.error || 'Failed to load companies');
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyDetails = useCallback(async (company: CompanyAccount) => {
    setLoadingDetails(true);
    setDetailTab('info');
    try {
      const result = await CompanyAccountService.getCompanyFull(company.id);
      if (result.success && result.data) {
        setSelectedCompany(result.data);
      } else {
        setSelectedCompany(company);
      }
    } catch (err) {
      setSelectedCompany(company);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  const handleSubmitForKYC = async () => {
    if (!selectedCompany) return;

    // Check for required documents
    const requiredDocs = ['certificate_of_incorporation'];
    const uploadedDocTypes = selectedCompany.documents?.map(d => d.documentType) || [];
    const missingDocs = requiredDocs.filter(doc => !uploadedDocTypes.includes(doc as any));

    if (missingDocs.length > 0) {
      const docNames = missingDocs.map(doc => doc.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
      const proceed = confirm(
        `The following recommended documents are missing:\n• ${docNames.join('\n• ')}\n\nYou can still submit, but KYC review may be delayed. Do you want to continue?`
      );
      if (!proceed) return;
    }

    // Confirm submission
    if (!confirm('Are you sure you want to submit this company for KYC review? You will not be able to edit the registration details until the review is complete.')) {
      return;
    }

    try {
      setSubmittingKYC(true);
      const result = await CompanyAccountService.submitForKYC(selectedCompany.id);
      if (result.success) {
        // Send KYC submitted notification email
        const primaryDirector = selectedCompany.officers?.find(o => o.customRoleTitle === 'Primary Director');
        sendKYCSubmittedEmail({
          companyName: selectedCompany.legalName,
          registrationNumber: selectedCompany.registrationNumber || '',
          directorName: primaryDirector ? `${(primaryDirector as any).firstName} ${(primaryDirector as any).lastName}` : 'Director',
          directorEmail: primaryDirector?.businessEmail || selectedCompany.primaryEmail || ''
        }).catch(err => console.error('Failed to send KYC submitted email:', err));

        alert('✅ Company submitted for KYC review successfully!\n\nYou will receive an email when the review is complete.');
        await loadCompanies();
        const updatedResult = await CompanyAccountService.getCompanyFull(selectedCompany.id);
        if (updatedResult.success && updatedResult.data) {
          setSelectedCompany(updatedResult.data);
        }
      } else {
        alert(result.error || 'Failed to submit for KYC');
      }
    } catch (err) {
      alert('An error occurred while submitting for KYC');
    } finally {
      setSubmittingKYC(false);
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleRegistrationSuccess = async (_companyId: string) => {
    setShowRegistrationForm(false);
    await loadCompanies();
    if (onRefreshData) {
      await onRefreshData();
    }
  };

  // Handle document deletion
  const handleDeleteDocument = async (doc: CompanyDocument) => {
    if (!selectedCompany) return;

    // Confirm deletion
    if (!confirm(`Are you sure you want to delete the document "${doc.documentName || doc.fileName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingDocId(doc.id);

      // Extract storage path from URL if file path exists
      let storagePath: string | null = null;
      if (doc.filePath) {
        const pathMatch = doc.filePath.match(/\/kyc\/(.+)$/);
        if (pathMatch) {
          storagePath = pathMatch[1];
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/user/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'delete_document',
          data: { documentId: doc.id, storagePath }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      // Refresh company details to show updated documents
      const updatedResult = await CompanyAccountService.getCompanyFull(selectedCompany.id);
      if (updatedResult.success && updatedResult.data) {
        setSelectedCompany(updatedResult.data);
      }

      alert('Document deleted successfully!');
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Failed to delete document. Please try again.');
    } finally {
      setDeletingDocId(null);
    }
  };

  // Validate logo file
  const validateLogoFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      return false;
    }

    if (file.size > maxSize) {
      alert('File size must be less than 2MB');
      return false;
    }

    return true;
  };

  // Handle logo file selection
  const handleLogoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && validateLogoFile(file)) {
      setSelectedLogoFile(file);
    } else {
      e.target.value = '';
    }
  };

  // Upload company logo
  const handleLogoUpload = async () => {
    if (!selectedLogoFile || !selectedCompany) return;

    try {
      setUploadingLogo(true);

      // Convert file to base64
      const reader = new FileReader();
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(selectedLogoFile);
      });

      const response = await fetch(`${API_BASE_URL}/api/user/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'upload_logo',
          data: {
            companyId: selectedCompany.id,
            fileBase64,
            fileName: selectedLogoFile.name,
            fileType: selectedLogoFile.type
          }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      // Refresh company details to show new logo
      const updatedResult = await CompanyAccountService.getCompanyFull(selectedCompany.id);
      if (updatedResult.success && updatedResult.data) {
        setSelectedCompany(updatedResult.data);
      }

      // Also refresh the companies list to show updated logo
      await loadCompanies();

      alert('Logo uploaded successfully!');
      setSelectedLogoFile(null);
      if (logoInputRef.current) logoInputRef.current.value = '';
    } catch (err) {
      console.error('Error uploading logo:', err);
      alert('Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Remove company logo
  const handleRemoveLogo = async () => {
    if (!selectedCompany || !selectedCompany.logoUrl) return;

    if (!confirm('Are you sure you want to remove the company logo?')) return;

    try {
      setUploadingLogo(true);

      const response = await fetch(`${API_BASE_URL}/api/user/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'remove_logo',
          data: {
            companyId: selectedCompany.id,
            logoUrl: selectedCompany.logoUrl
          }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      // Refresh company details
      const updatedResult = await CompanyAccountService.getCompanyFull(selectedCompany.id);
      if (updatedResult.success && updatedResult.data) {
        setSelectedCompany(updatedResult.data);
      }

      // Also refresh the companies list
      await loadCompanies();

      alert('Logo removed successfully!');
    } catch (err) {
      console.error('Error removing logo:', err);
      alert('Failed to remove logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-700 rounded w-1/3"></div>
            <div className="h-4 bg-gray-700 rounded w-2/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="h-32 bg-gray-700 rounded"></div>
              <div className="h-32 bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show registration form
  if (showRegistrationForm) {
    return (
      <CompanyRegistrationForm
        userId={userId}
        onSuccess={handleRegistrationSuccess}
        onCancel={() => setShowRegistrationForm(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="text-3xl">🏢</span>
              Company Accounts
            </h2>
            <p className="text-white mt-1">
              Manage your company shareholdings and corporate accounts
            </p>
          </div>
          <button
            onClick={() => setShowRegistrationForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            <span>+</span>
            <span>Register Company</span>
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-900/30 border border-red-500 rounded-lg p-4">
          <p className="text-red-400">{error}</p>
          <button
            onClick={loadCompanies}
            className="mt-2 text-red-300 hover:text-red-200 underline text-sm"
          >
            Try again
          </button>
        </div>
      )}

      {/* Empty state */}
      {companies.length === 0 && !error && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-12 text-center">
          <div className="text-6xl mb-4">🏢</div>
          <h3 className="text-xl font-semibold text-white mb-2">No Company Accounts</h3>
          <p className="text-white mb-6 max-w-md mx-auto">
            Register your company to purchase shares on behalf of your business.
            Company accounts allow for corporate share purchases and separate portfolio management.
          </p>
          <button
            onClick={() => setShowRegistrationForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Register Your First Company
          </button>
        </div>
      )}

      {/* Company list */}
      {companies.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {companies.map((company) => {
            const statusConfig = kycStatusConfig[company.kycStatus];

            return (
              <div
                key={company.id}
                className="bg-gray-800 rounded-lg border border-gray-700 p-6 hover:border-gray-600 transition-colors cursor-pointer"
                onClick={() => setSelectedCompany(company)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    {company.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt={company.legalName}
                        className="w-14 h-14 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-14 h-14 bg-blue-600/20 rounded-lg flex items-center justify-center text-blue-400 font-bold text-xl">
                        {company.legalName.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-white">{company.legalName}</h3>
                      {company.tradingName && company.tradingName !== company.legalName && (
                        <p className="text-white text-sm">Trading as: {company.tradingName}</p>
                      )}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.color} flex items-center gap-1`}>
                    <span>{statusConfig.icon}</span>
                    <span>{statusConfig.label}</span>
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-white">Registration:</span>
                    <span className="text-white ml-2">{company.registrationNumber}</span>
                  </div>
                  <div>
                    <span className="text-white">Type:</span>
                    <span className="text-white ml-2 capitalize">{company.companyType.replace(/_/g, ' ')}</span>
                  </div>
                  {company.industryName && (
                    <div className="col-span-2">
                      <span className="text-white">Industry:</span>
                      <span className="text-white ml-2">{company.industryName}</span>
                    </div>
                  )}
                </div>

                {/* Quick actions */}
                <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between">
                  <div className="text-white text-sm">
                    Created {new Date(company.createdAt).toLocaleDateString()}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      loadCompanyDetails(company);
                    }}
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                  >
                    View Details →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Company Detail Modal */}
      {selectedCompany && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-900 rounded-xl border border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-900 px-6 py-4 border-b border-gray-700 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                {selectedCompany.logoUrl ? (
                  <img src={selectedCompany.logoUrl} alt="" className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center text-2xl">🏢</div>
                )}
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedCompany.legalName}</h3>
                  {selectedCompany.tradingName && <p className="text-sm text-white">t/a {selectedCompany.tradingName}</p>}
                </div>
              </div>
              <button onClick={() => setSelectedCompany(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>

            {loadingDetails ? (
              <div className="p-6 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full"></div>
                <span className="ml-3 text-white">Loading details...</span>
              </div>
            ) : (
              <>
                {/* KYC Status Banner */}
                <div className={`px-6 py-3 ${kycStatusConfig[selectedCompany.kycStatus].bgColor}`}>
                  <div className="flex items-center justify-between">
                    <span className={`flex items-center gap-2 ${kycStatusConfig[selectedCompany.kycStatus].color}`}>
                      {kycStatusConfig[selectedCompany.kycStatus].icon} KYC Status: {kycStatusConfig[selectedCompany.kycStatus].label}
                    </span>
                    {selectedCompany.kycStatus === 'draft' && (
                      <button
                        onClick={handleSubmitForKYC}
                        disabled={submittingKYC}
                        className="px-4 py-1 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg text-sm transition-colors disabled:opacity-50"
                      >
                        {submittingKYC ? 'Submitting...' : 'Submit for Review'}
                      </button>
                    )}
                  </div>
                  {selectedCompany.kycRejectionReason && (
                    <p className="mt-2 text-sm text-red-400">Reason: {selectedCompany.kycRejectionReason}</p>
                  )}
                </div>

                {/* Tab Navigation */}
                <div className="px-6 pt-4 border-b border-gray-700">
                  <div className="flex gap-4">
                    {[
                      { key: 'info' as DetailTab, label: 'Company Info', icon: '🏢' },
                      { key: 'officers' as DetailTab, label: `Officers (${selectedCompany.officers?.length || 0})`, icon: '👥' },
                      { key: 'documents' as DetailTab, label: `Documents (${selectedCompany.documents?.length || 0})`, icon: '📄' },
                    ].map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setDetailTab(tab.key)}
                        className={`pb-3 px-2 font-medium transition-colors border-b-2 ${
                          detailTab === tab.key
                            ? 'border-yellow-400 text-yellow-400'
                            : 'border-transparent text-gray-400 hover:text-white'
                        }`}
                      >
                        {tab.icon} {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {detailTab === 'info' && (
                    <div className="space-y-6">
                      {/* Company Logo Upload Section */}
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-white mb-3">🖼️ Company Logo</h4>
                        <div className="flex items-center gap-6">
                          {/* Current Logo Display */}
                          <div className="flex-shrink-0">
                            {selectedCompany.logoUrl ? (
                              <img
                                src={selectedCompany.logoUrl}
                                alt={selectedCompany.legalName}
                                className="w-24 h-24 rounded-lg object-cover border border-gray-600"
                              />
                            ) : (
                              <div className="w-24 h-24 rounded-lg bg-gray-700 flex items-center justify-center border border-gray-600 border-dashed">
                                <span className="text-gray-500 text-3xl">🏢</span>
                              </div>
                            )}
                          </div>

                          {/* Upload Controls */}
                          <div className="flex-1 space-y-3">
                            <p className="text-sm text-white">
                              Upload a company logo (JPG, PNG, GIF, or WebP, max 2MB)
                            </p>

                            <div className="flex flex-wrap items-center gap-3">
                              <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                onChange={handleLogoFileSelect}
                                className="hidden"
                                id="logo-upload-input"
                              />
                              <label
                                htmlFor="logo-upload-input"
                                className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm cursor-pointer transition-colors inline-flex items-center gap-2"
                              >
                                📁 Select File
                              </label>

                              {selectedLogoFile && (
                                <>
                                  <span className="text-sm text-white truncate max-w-[150px]">
                                    {selectedLogoFile.name}
                                  </span>
                                  <button
                                    onClick={handleLogoUpload}
                                    disabled={uploadingLogo}
                                    className="px-3 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg text-sm transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                                  >
                                    {uploadingLogo ? '⏳ Uploading...' : '📤 Upload'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedLogoFile(null);
                                      if (logoInputRef.current) logoInputRef.current.value = '';
                                    }}
                                    className="px-2 py-2 text-gray-400 hover:text-white text-sm transition-colors"
                                  >
                                    ✕
                                  </button>
                                </>
                              )}

                              {selectedCompany.logoUrl && !selectedLogoFile && (
                                <button
                                  onClick={handleRemoveLogo}
                                  disabled={uploadingLogo}
                                  className="px-3 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg text-sm transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                                >
                                  {uploadingLogo ? '⏳...' : '🗑️ Remove Logo'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-gray-800 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-white mb-3">Company Details</h4>
                          <dl className="space-y-2 text-sm">
                            <div className="flex justify-between"><dt className="text-white">Registration Number</dt><dd className="text-white">{selectedCompany.registrationNumber || 'N/A'}</dd></div>
                            <div className="flex justify-between"><dt className="text-white">Tax Number</dt><dd className="text-white">{selectedCompany.taxIdentificationNumber || 'N/A'}</dd></div>
                            <div className="flex justify-between"><dt className="text-white">Company Type</dt><dd className="text-white capitalize">{selectedCompany.companyType?.replace(/_/g, ' ') || 'N/A'}</dd></div>
                            <div className="flex justify-between"><dt className="text-white">Date of Incorporation</dt><dd className="text-white">{formatDate(selectedCompany.dateOfIncorporation)}</dd></div>
                            <div className="flex justify-between"><dt className="text-white">Industry</dt><dd className="text-white">{selectedCompany.industryName || 'N/A'}</dd></div>
                            <div className="flex justify-between"><dt className="text-white">Jurisdiction</dt><dd className="text-white">{selectedCompany.jurisdictionCountryCode || 'N/A'}</dd></div>
                          </dl>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-white mb-3">Contact Information</h4>
                          <dl className="space-y-2 text-sm">
                            <div className="flex justify-between"><dt className="text-white">Email</dt><dd className="text-white">{selectedCompany.primaryEmail || 'N/A'}</dd></div>
                            <div className="flex justify-between"><dt className="text-white">Phone</dt><dd className="text-white">{selectedCompany.primaryPhone || 'N/A'}</dd></div>
                            <div className="flex justify-between"><dt className="text-white">Website</dt><dd className="text-white">{selectedCompany.websiteUrl || 'N/A'}</dd></div>
                          </dl>
                        </div>
                      </div>
                      {selectedCompany.registeredAddress && (
                        <div className="bg-gray-800 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-white mb-3">Registered Address</h4>
                          <p className="text-white text-sm">
                            {selectedCompany.registeredAddress.street}, {selectedCompany.registeredAddress.city}, {selectedCompany.registeredAddress.province} {selectedCompany.registeredAddress.postalCode}, {selectedCompany.registeredAddress.countryCode}
                          </p>
                        </div>
                      )}
                      {selectedCompany.businessDescription && (
                        <div className="bg-gray-800 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-white mb-3">Business Description</h4>
                          <p className="text-white text-sm">{selectedCompany.businessDescription}</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-700">
                        <button
                          onClick={() => setShowDetailsEditor(true)}
                          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                          ✏️ Edit Company Details
                        </button>
                        {selectedCompany.kycStatus === 'approved' && (
                          <button
                            onClick={() => setShowShareTransfer(true)}
                            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-black font-medium rounded-lg transition-colors flex items-center gap-2"
                          >
                            🏢 Transfer Shares to Company
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {detailTab === 'officers' && (
                    <div className="space-y-4">
                      {/* Manage Officers Button */}
                      <div className="flex justify-end">
                        <button
                          onClick={() => setShowOfficerManagement(true)}
                          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                          👥 Manage Officers
                        </button>
                      </div>

                      {selectedCompany.officers && selectedCompany.officers.length > 0 ? (
                        selectedCompany.officers.map((officer) => (
                          <div key={officer.id} className="bg-gray-800 rounded-lg p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center text-xl">👤</div>
                              <div>
                                <p className="text-white font-medium">{(officer as any).firstName} {(officer as any).lastName}</p>
                                <p className="text-sm text-white">{officer.customRoleTitle || officer.role}</p>
                                {officer.businessEmail && <p className="text-sm text-gray-500">{officer.businessEmail}</p>}
                              </div>
                            </div>
                            <div className="text-right">
                              {officer.shareholdingPercentage > 0 && <p className="text-yellow-400">{officer.shareholdingPercentage}% shares</p>}
                              {officer.kycVerified ? (
                                <span className="text-green-400 text-sm">✓ KYC Verified</span>
                              ) : (
                                <span className="text-white text-sm">Pending KYC</span>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-white">
                          <span className="text-4xl mb-3 block">👥</span>
                          <p>No officers on record</p>
                          <p className="text-sm mt-2">Click the button above to add officers</p>
                        </div>
                      )}
                    </div>
                  )}

                  {detailTab === 'documents' && (
                    <div className="space-y-4">
                      {/* Upload Button */}
                      <div className="flex justify-end">
                        <button
                          onClick={() => setShowDocumentUpload(true)}
                          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                          📤 Upload Document
                        </button>
                      </div>

                      {selectedCompany.documents && selectedCompany.documents.length > 0 ? (
                        selectedCompany.documents.map((doc) => (
                          <div key={doc.id} className="bg-gray-800 rounded-lg p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center text-2xl">📄</div>
                              <div>
                                <p className="text-white font-medium">{doc.documentName || doc.documentType}</p>
                                <p className="text-sm text-white">{doc.fileName}</p>
                                <p className="text-sm text-gray-500">Uploaded {formatDate(doc.uploadedAt)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-sm ${
                                doc.verificationStatus === 'approved' ? 'bg-green-900/30 text-green-400' :
                                doc.verificationStatus === 'rejected' ? 'bg-red-900/30 text-red-400' :
                                'bg-yellow-900/30 text-yellow-400'
                              }`}>
                                {doc.verificationStatus || 'pending'}
                              </span>
                              {/* Only allow deletion of pending documents */}
                              {doc.verificationStatus !== 'approved' && (
                                <button
                                  onClick={() => handleDeleteDocument(doc)}
                                  disabled={deletingDocId === doc.id}
                                  className="px-2 py-1 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                  title="Delete document"
                                >
                                  {deletingDocId === doc.id ? '⏳' : '🗑️'}
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-white">
                          <span className="text-4xl mb-3 block">📁</span>
                          <p>No documents uploaded yet</p>
                          <p className="text-sm mt-2">Click the button above to upload your first document</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {/* Document Upload Modal */}
      {showDocumentUpload && selectedCompany && (
        <CompanyDocumentUpload
          companyId={selectedCompany.id}
          userId={userId}
          onUploadComplete={async () => {
            setShowDocumentUpload(false);
            // Refresh company details to show new document
            const updatedResult = await CompanyAccountService.getCompanyFull(selectedCompany.id);
            if (updatedResult.success && updatedResult.data) {
              setSelectedCompany(updatedResult.data);
            }
          }}
          onClose={() => setShowDocumentUpload(false)}
        />
      )}

      {/* Officer Management Modal */}
      {showOfficerManagement && selectedCompany && (
        <OfficerManagement
          companyId={selectedCompany.id}
          companyName={selectedCompany.legalName}
          officers={selectedCompany.officers || []}
          onOfficerAdded={async () => {
            // Refresh company details to show updated officers
            const updatedResult = await CompanyAccountService.getCompanyFull(selectedCompany.id);
            if (updatedResult.success && updatedResult.data) {
              setSelectedCompany(updatedResult.data);
            }
          }}
          onClose={() => setShowOfficerManagement(false)}
        />
      )}

      {/* Company Details Editor Modal */}
      {showDetailsEditor && selectedCompany && (
        <CompanyDetailsEditor
          company={selectedCompany}
          onSave={async () => {
            // Refresh company details after save
            const updatedResult = await CompanyAccountService.getCompanyFull(selectedCompany.id);
            if (updatedResult.success && updatedResult.data) {
              setSelectedCompany(updatedResult.data);
            }
            await loadCompanies();
          }}
          onClose={() => setShowDetailsEditor(false)}
        />
      )}

      {/* Share Transfer to Company Modal */}
      {showShareTransfer && selectedCompany && (
        <ShareTransferToCompany
          company={selectedCompany}
          userId={userId}
          onTransferComplete={async () => {
            // Refresh company details after transfer
            const updatedResult = await CompanyAccountService.getCompanyFull(selectedCompany.id);
            if (updatedResult.success && updatedResult.data) {
              setSelectedCompany(updatedResult.data);
            }
            if (onRefreshData) {
              await onRefreshData();
            }
          }}
          onClose={() => setShowShareTransfer(false)}
        />
      )}
    </div>
  );
};

export default CompanyDashboardSection;

